/* eslint-disable jsx-a11y/img-redundant-alt */
import React, { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { Link } from 'react-router-dom'
import MoreServicesSection from './MoreServicesSection'
import FooterCTA from './FooterCTA'
import warehousing from '../assets/images/data/warehouse.png'
import Professional_work from '../assets/images/data/Professional-work.jpg'
import Franchise_Partner from '../assets/images/data/Franchise_Partner.jpg'
import FAQ from '../assets/images/data/FAQ.jpg'
import { Helmet } from 'react-helmet-async'

const BusinessAccountPage = () => {
  const [expandedFaq, setExpandedFaq] = useState(null)

  const faqs = [
    {
      question: 'How do you use my contact information?',
      answer:
        'We use your contact information to provide you with shipping updates, account notifications, and customer support. Your information is kept secure and never shared with third parties without your consent.'
    },
    {
      question:
        'I want to speak with someone. Why do I have to fill out the shipping profile to get connected?',
      answer:
        'Filling out your shipping profile helps us connect you with the right specialist who understands your specific needs. This ensures you get the most relevant advice and solutions for your business.'
    },
    {
      question:
        'What different business shipping methods does S Infinite Express offer?',
      answer:
        'We offer express delivery, standard shipping, bulk cargo transport, same-day delivery, international shipping, and specialized logistics for industries like healthcare, automotive, and e-commerce.'
    },
    {
      question:
        'What benefits do I get from opening a business account with S Infinite Express?',
      answer:
        'Business account holders enjoy preferential rates, dedicated customer support, advanced tracking tools, bulk shipping discounts, priority processing, and access to our exclusive business portal.'
    },
    {
      question: 'How much does shipping cost with S Infinite Express?',
      answer:
        'Shipping costs vary based on package size, weight, destination, and service type. Business account holders receive competitive rates with volume discounts. Contact us for a custom quote based on your shipping needs.'
    },
    {
      question: 'Does S Infinite Express offer shipping for small businesses?',
      answer:
        'Yes! We welcome businesses of all sizes. Our flexible solutions are designed to grow with your business, from startups to large enterprises. We offer scalable pricing and services to match your needs.'
    }
  ]

  return (
    <div className='min-h-screen bg-gray-50'>
      <Helmet>
        <title>Business Logistics Solutions | S Infinite Express</title>
        <meta
          name='description'
          content='Unlock scalable business logistics with S Infinite Express. From franchising and warehousing to tailored delivery services, grow smarter with our solutions.'
        />
        <meta
          name='keywords'
          content='Business logistics India, S Infinite Express business account, warehousing services, franchise opportunities, scalable shipping, SME logistics solutions, logistics for businesses, enterprise courier solutions, pan-India shipping partner, commercial delivery services, logistics support for startups, B2B logistics India, custom shipping plans, warehouse fulfillment India'
        />
        <meta name='robots' content='index, follow' />
        <link
          rel='canonical'
          href='https://sinfiniteexpress.com/business-account'
        />

        <meta
          property='og:title'
          content='Business Logistics Solutions | S Infinite Express'
        />
        <meta
          property='og:description'
          content='Empower your business with flexible and scalable logistics solutions from S Infinite Express — including warehousing, franchising, and pan-India delivery services.'
        />
        <meta
          property='og:url'
          content='https://sinfiniteexpress.com/business-account'
        />
        <meta property='og:type' content='website' />
      </Helmet>

      {/* Hero Section */}
      <section className='bg-gradient-to-r from-[#fff44f] via-[#fff86f] to-[#fffcb0] py-20 px-4'>
        <div className='max-w-6xl mx-auto'>
          {/* Main Headline */}
          <div className='text-center mb-16'>
            <h2 className='text-3xl md:text-4xl font-extrabold'>
              <span className='text-[#211f57]'>Explore Our </span>
              <span className='text-red-600'>Business Solutions</span>
            </h2>
            <p className='mt-4 text-gray-700 max-w-2xl mx-auto text-lg'>
              From franchising opportunities and tailored logistics support to
              scalable warehousing, discover how S Infinite Express can
              accelerate your growth.
            </p>
            <div className='w-20 h-1 bg-red-600 mx-auto mt-4 rounded-full'></div>
          </div>

          {/* Franchise Section */}
          <div className='grid md:grid-cols-2 gap-10 items-center mb-20'>
            <div>
              <h3 className='text-3xl font-bold text-gray-900 mb-4'>
                Looking to grow your business with{' '}
                <span className='text-red-600'>S Infinite Express</span>?
              </h3>
              <div className='w-16 h-1 bg-red-600 rounded-full mb-6'></div>
              <p className='text-gray-700 leading-relaxed mb-6 text-lg'>
                With S Infinite Express by your side, you’ll have dynamic
                logistics software, scalable warehousing solutions, hands-on
                technical support, and the strength of a trusted national
                brand—so you can grow faster and serve your customers better.
                From seamless last-mile delivery and real-time shipment tracking
                to data-driven insights and dedicated account management, we
                equip you with everything you need to streamline your
                operations, delight your customers, and unlock new opportunities
                across India.
              </p>
              <Link
                to='/join-us/franchise'
                className='inline-block bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transform hover:scale-105 transition'
              >
                Learn About Franchising
              </Link>
            </div>
            <div>
              <img
                src={Franchise_Partner}
                alt='Franchise Partner'
                className='rounded-xl shadow-lg w-full h-72 object-cover'
              />
            </div>
          </div>

          {/* Let Us Work Section */}
          <div className='grid md:grid-cols-2 gap-10 items-center mb-20'>
            <div>
              <img
                src={Professional_work}
                alt='Professional at work'
                className='rounded-xl shadow-lg w-full h-72 object-cover'
              />
            </div>
            <div>
              <h3 className='text-3xl font-bold text-gray-900 mb-4'>
                Let <span className='text-red-600'>S Infinite Express</span>{' '}
                work for you
              </h3>
              <div className='w-16 h-1 bg-red-600 rounded-full mb-6'></div>
              <p className='text-gray-700 leading-relaxed mb-6 text-lg'>
                From your first shipment to nationwide growth, S Infinite
                Express is the logistics partner you can count on—so you never
                have to worry about a thing. We handle every detail for you:
                real-time tracking, secure warehousing, smooth last-mile
                delivery, and the dedicated support you deserve. We’re here to
                take care of everything behind the scenes. Need a quick
                estimate?{' '}
                <a
                  href='/get-a-quote'
                  className='text-red-600 hover:underline font-medium'
                >
                  Get a Quote
                </a>{' '}
                anytime and get moving faster.
              </p>
              <Link
                to='/join-us/client-registration'
                className='inline-block bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transform hover:scale-105 transition'
              >
                Let’s Connect About Your Needs
              </Link>
            </div>
          </div>

          {/* Warehousing Section */}
          <div className='grid md:grid-cols-2 gap-10 items-center'>
            <div>
              <h3 className='text-3xl font-bold text-gray-900 mb-4'>
                Store Smarter. Deliver Faster.
              </h3>
              <div className='w-16 h-1 bg-red-600 rounded-full mb-6'></div>
              <p className='text-gray-700 leading-relaxed mb-6 text-lg'>
                Ready to expand your reach and scale with confidence? S Infinite
                Express has you covered with secure, tech-enabled warehousing
                solutions designed to grow alongside your business. Our modern
                facilities give you end-to-end control—from inventory management
                and order fulfillment to real-time visibility—so you can
                distribute faster and scale without limits across India.
              </p>
              <a
                href='/services/warehousing'
                className='inline-block bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transform hover:scale-105 transition'
              >
                Start Your Warehousing Journey
              </a>
            </div>
            <div>
              <img
                src={warehousing}
                alt='Warehouse operations'
                className='rounded-xl shadow-lg w-full h-72 object-cover'
              />
            </div>
          </div>
        </div>
      </section>

      <MoreServicesSection />

      {/* FAQ Section */}
      <section className='bg-white py-16 px-4'>
        <div className='max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center'>
          {/* Left: Image */}
          <div className='relative'>
            <img
              src={FAQ}
              alt='FAQ Image'
              className='rounded-lg shadow-lg w-full h-96 object-cover'
            />
          </div>

          {/* Right: FAQ Accordion */}
          <div>
            <h2 className='text-3xl font-bold text-gray-900 mb-2'>
              Frequently Asked Questions
            </h2>
            <div className='w-16 h-1 bg-red-600 rounded-full mb-6'></div>

            <div className='divide-y divide-gray-200'>
              {faqs.map((faq, index) => (
                <div key={index} className='py-4'>
                  <button
                    onClick={() =>
                      setExpandedFaq(expandedFaq === index ? null : index)
                    }
                    className='w-full flex items-center justify-between text-left hover:text-red-600 transition-colors'
                  >
                    <span className='font-medium text-gray-900 text-lg'>
                      {faq.question}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 text-gray-400 transition-transform ${
                        expandedFaq === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {expandedFaq === index && (
                    <div className='mt-3 text-gray-600 leading-relaxed'>
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <FooterCTA />
    </div>
  )
}

export default BusinessAccountPage
